import {
  type LoaderData,
  useLoaderData,
  useNavigation,
  type LoaderFunction,
  wordpressLoader,
  json,
  redirect,
  WpController
} from '@ubo/losse-sjedel'
import { useEffect } from 'react'
import progress from 'nprogress'
import FlexContentHandler from '~/components/flex/FlexContentHandler'
import Layout from '~/components/layout/Layout'
import type { RedirectionItemType } from '~/graphql/types'

export const loader: LoaderFunction = async (data) => {
  const url = new URL(data.request.url)

  const searchParams = url.searchParams
  const lang = searchParams.get('lang')

  if (lang === 'en' && !url.pathname.includes('/en/')) {
    return redirect(`${url.origin}/en${url.pathname}`)
  }

  try {
    const page = await wordpressLoader(data)

    return json(page.body, page.headers)
  } catch (error) {
    const wpController = new WpController()
    await wpController.init()

    const query = await wpController.query(`
      query Redirects {
        Redirects: redirections {
          target
          url
          code
        }
      }
    `)

    const redirection = query?.Redirects?.find(
      (_redirect: RedirectionItemType) => removeLeadingAndTrailingSlashes(_redirect.url) === removeLeadingAndTrailingSlashes(url.pathname)
    )

    if (redirection) {
      return redirect(redirection.target, redirection.code)
    }

    throw new Response(null, {
      status: 404
    })
  }
}

function removeLeadingAndTrailingSlashes(str: string) {
  return str.replace(/^\/+|\/+$/g, '')
}

export default function $page() {
  const {
    page: { contentType, flexContent }
  } = useLoaderData<LoaderData>()

  const transition = useNavigation()

  // JSON-LD injection for all public/json/*.json based on their url field
  useEffect(() => {
    const jsonLdFiles = [
      { file: '/json/diensten-faq.json', url: '/diensten/' },
      { file: '/json/diensten-service.json', url: '/diensten/' },
      {
        file: '/json/faq-microsoft365-itbeheer-cloud.json',
        url: '/blog/alles-over-microsoft-365-it-beheer-en-cloudoplossingen-voor-mkb/'
      },
      { file: '/json/it-beheer-faq.json', url: '/diensten/beheer/' },
      { file: '/json/it-beheer.json', url: '/diensten/beheer/' },
      { file: '/json/it-beveiliging-faq.json', url: '/diensten/beveiliging/' },
      { file: '/json/it-beveiliging.json', url: '/diensten/beveiliging/' },
      { file: '/json/localbusiness.json', url: '/' },
      { file: '/json/microsoft365-migratie-faq.json', url: '/diensten/migratie/' },
      { file: '/json/microsoft365-migratie.json', url: '/diensten/migratie/' },
      { file: '/json/moderne-werkplek-faq.json', url: '/diensten/moderne-werkplek/' },
      { file: '/json/moderne-werkplek.json', url: '/diensten/moderne-werkplek/' },
      { file: '/json/reviews.json', url: '/reviews/' }
    ]
    const currentUrl = window.location.pathname
    const matches = jsonLdFiles.filter((j) => j.url === currentUrl)
    // Remove previously injected scripts for this url
    matches.forEach((match) => {
      const prev = document.head.querySelector(`script[data-injected-jsonld="${match.file}"]`)
      if (prev) document.head.removeChild(prev)
    })
    matches.forEach((match) => {
      // Prevent duplicate injection
      if (document.head.querySelector(`script[data-injected-jsonld="${match.file}"]`)) {
        return
      }
      fetch(match.file)
        .then((res) => res.json())
        .then((data) => {
          const script = document.createElement('script')
          script.type = 'application/ld+json'
          script.text = JSON.stringify(data)
          script.setAttribute('data-injected-jsonld', match.file)
          document.head.appendChild(script)
        })
    })
    // Cleanup on unmount
    return () => {
      matches.forEach((match) => {
        const script = document.head.querySelector(`script[data-injected-jsonld="${match.file}"]`)
        if (script) document.head.removeChild(script)
      })
    }
  }, [])

  useEffect(() => {
    if (transition.state === 'loading' || transition.state === 'submitting') {
      progress.start()
    } else {
      progress.done()
    }
  }, [transition.state])

  return (
    <Layout>
      <FlexContentHandler prefix={`${contentType?.node?.name}_Flexcontent`} flex={flexContent?.flex as any} />
    </Layout>
  )
}
